import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

import { toast } from 'sonner';
import { safeFetch } from '../../config/api';

const RELOCATION_TYPES = [
  { value: 'company_setup', label: 'Company Setup' },
  { value: 'real_estate', label: 'Real Estate' },
];

const BUDGET_OPTIONS = [
  { value: 'under-30k', label: 'Under $30,000' },
  { value: '30-100k', label: '$30,000 - $100,000' },
  { value: 'over-100k', label: 'Over $100,000' },
];

const TIMELINE_OPTIONS = [
  { value: '0-3-months', label: '0-3 months' },
  { value: '3-6-months', label: '3-6 months' },
  { value: '6-12-months', label: '6-12 months' },
  { value: '1-year-plus', label: '1+ year' },
  { value: 'exploring', label: 'Just exploring' },
];

export const LeadFormModal = ({ isOpen, onClose, sourcePage = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    current_country: '',
    target_country: '',
    relocation_type: '',
    property_interest: 'no',
    planning_company: 'no',
    budget_range: '',
    timeline: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await safeFetch(`/api/countries/list`);
        if (res.ok) {
          const data = await res.json();
          setCountries(data);
        }
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrors({});
    }
  }, [isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!formData.relocation_type) newErrors.relocation_type = 'Relocation type is required';
    if (!formData.target_country) newErrors.target_country = 'Target country is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Determine secondary interest based on questions
  const getSecondaryInterest = () => {
    if (formData.relocation_type === 'company_setup' && formData.property_interest !== 'no') {
      return 'real_estate';
    }
    if (formData.relocation_type === 'real_estate' && formData.planning_company !== 'no') {
      return 'company_setup';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);

    try {
      const secondary_interest = getSecondaryInterest();
      
      const response = await safeFetch(`/api/b2b/leads/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          current_country: formData.current_country,
          target_country: formData.target_country,
          relocation_type: formData.relocation_type,
          service_type: formData.relocation_type, // Primary source of truth
          secondary_interest: secondary_interest,
          property_interest: formData.property_interest,
          planning_company: formData.planning_company,
          budget_range: formData.budget_range,
          timeline: formData.timeline,
          notes: formData.notes,
          source_page: sourcePage || 'relocation_form',
        }),
      });

      if (response.ok) {
        toast.success('Thank you! We\'ll be in touch soon.');
        onClose();
        setFormData({
          name: '',
          email: '',
          phone: '',
          current_country: '',
          target_country: '',
          relocation_type: '',
          property_interest: 'no',
          planning_company: 'no',
          budget_range: '',
          timeline: '',
          notes: '',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-fadeIn" data-testid="lead-form-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-slate-100 flex justify-between items-center z-10">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Get Expert Help</h2>
            <p className="text-sm text-slate-500">Company setup & Real estate services</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-lead-form"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 1. Name and Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                data-testid="lead-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                data-testid="lead-email-input"
              />
            </div>
          </div>

          {/* Phone - Required */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone / WhatsApp *</Label>
            <Input
              id="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1 234 567 8900"
              className={errors.phone ? 'border-red-500' : ''}
              data-testid="lead-phone-input"
            />
            {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
          </div>

          {/* 2. Country Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="current_country">Current Country</Label>
              <select
                id="current_country"
                value={formData.current_country}
                onChange={(e) => setFormData({ ...formData, current_country: e.target.value })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                data-testid="lead-current-country-select"
              >
                <option value="">Select country</option>
                {countries.map(country => (
                  <option key={country.slug} value={country.name}>{country.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="target_country">Target Country *</Label>
              <select
                id="target_country"
                required
                value={formData.target_country}
                onChange={(e) => setFormData({ ...formData, target_country: e.target.value })}
                className={`w-full h-10 px-3 border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent ${errors.target_country ? 'border-red-500' : 'border-slate-200'}`}
                data-testid="lead-target-country-select"
              >
                <option value="">Select country</option>
                {countries.map(country => (
                  <option key={country.slug} value={country.name}>{country.name}</option>
                ))}
              </select>
              {errors.target_country && <p className="text-xs text-red-500">{errors.target_country}</p>}
            </div>
          </div>

          {/* 3. Relocation Type (PRIMARY FIELD) */}
          <div className="space-y-2">
            <Label htmlFor="relocation_type">Relocation Type *</Label>
            <select
              id="relocation_type"
              required
              value={formData.relocation_type}
              onChange={(e) => setFormData({ ...formData, relocation_type: e.target.value })}
              className={`w-full h-10 px-3 border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent ${errors.relocation_type ? 'border-red-500' : 'border-slate-200'}`}
              data-testid="lead-relocation-type-select"
            >
              <option value="">Select your primary interest</option>
              {RELOCATION_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {errors.relocation_type && <p className="text-xs text-red-500">{errors.relocation_type}</p>}
          </div>

          {/* 4. Secondary Service Questions */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <p className="text-sm font-medium text-slate-700">Additional interests (optional)</p>
            
            {/* Property Interest - Only show if relocation_type is company_setup */}
            {formData.relocation_type !== 'real_estate' && (
              <div className="space-y-2">
                <Label className="text-slate-600">Are you also interested in buying property?</Label>
                <div className="flex gap-2">
                  {['yes', 'maybe', 'no'].map(option => (
                    <button
                      key={`property-${option}`}
                      type="button"
                      onClick={() => setFormData({ ...formData, property_interest: option })}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                        formData.property_interest === option
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'
                      }`}
                      data-testid={`property-${option}-btn`}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Company Interest - Only show if relocation_type is real_estate */}
            {formData.relocation_type !== 'company_setup' && (
              <div className="space-y-2">
                <Label className="text-slate-600">Are you also interested in starting a company?</Label>
                <div className="flex gap-2">
                  {['yes', 'maybe', 'no'].map(option => (
                    <button
                      key={`company-${option}`}
                      type="button"
                      onClick={() => setFormData({ ...formData, planning_company: option })}
                      className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                        formData.planning_company === option
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-400'
                      }`}
                      data-testid={`company-${option}-btn`}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Show both if no relocation type selected yet */}
            {!formData.relocation_type && (
              <p className="text-xs text-slate-400 italic">Select a relocation type above to see relevant questions</p>
            )}
          </div>

          {/* 5. Budget and Timeline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Budget Range</Label>
              <select
                value={formData.budget_range}
                onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                data-testid="lead-budget-select"
              >
                <option value="">Select budget</option>
                {BUDGET_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Timeline</Label>
              <select
                value={formData.timeline}
                onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                data-testid="lead-timeline-select"
              >
                <option value="">When?</option>
                {TIMELINE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 6. Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Tell us more</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any specific questions or requirements?"
              rows={3}
              data-testid="lead-notes-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-slate-900 text-white hover:bg-slate-800 h-11"
            disabled={loading}
            data-testid="lead-submit-btn"
          >
            {loading ? 'Submitting...' : 'Get Expert Help'}
          </Button>

          <p className="text-xs text-center text-slate-500">
            We'll respond within 24 hours. No spam, ever.
          </p>
        </form>
      </div>
    </div>
  );
};
